import { useDispatch, useSelector } from "react-redux";

import DeviceProps from "@magnus/react-native-device-props";

import Analytics from "@web-solutions/module-analytics";

import { useAppSelector } from "@web-solutions/store/types";

import { OneTimeProduct, Subscription } from "core/interfaces/billing";
import { PaymentProcessorRef } from "core/payment/payment-processor-provider/components/payment-processor";
import { extractPaymentErrorMessage } from "core/payment/utils/extract-payment-error-message";
import { selectOneTimePurchases, selectPaymentSystem } from "core/store/billing/selectors";
import { OneTimePurchase } from "core/store/billing";
import {
  createOneClickFreePayment,
  setOneTimePurchaseId,
  setOneTimePurchases,
} from "core/store/billing/actions";
import { recordTransaction } from "core/upsale/api/transaction";

import { nextStep, setIsLoading } from "core/upsale/store";
import { sendEmail } from "core/upsale/api/send-email";
import { useLocale } from "core/hooks/use-locale";
import { PaymentSystem } from "@web-solutions/react-billing/constants";

interface PaymentHandlersProps {
  activeProduct?: OneTimeProduct;
  onSuccess: (data: any) => void;
  onError: (error: any) => void;
  onOneClickError: (error: any) => void;
  ppRef: React.RefObject<PaymentProcessorRef | null>;
}

export const usePaymentHandlers = ({
  activeProduct,
  onSuccess,
  onError,
  onOneClickError,
  ppRef,
}: PaymentHandlersProps) => {
  const dispatch = useDispatch<any>();

  const oneTimePurchases = useSelector(selectOneTimePurchases);
  const paymentSystem = useSelector(selectPaymentSystem);
  const purchase = useAppSelector((state) => state.billing.purchase);
  const { locale } = useLocale();

  const onUpsaleSuccess = (email?: string) => {
    const sendMessage = async () => {
      if (activeProduct?.messageTemplate && email) {
        await sendEmail(email, `${activeProduct.messageTemplate}_${locale}`);

        Analytics.trackEvent("email", "upsale_purchase", {
          product_name: activeProduct.productCode,
          billing_date: new Date().toISOString().split("T")[0],
        });
      }
    };

    sendMessage().finally(() => {
      Analytics.trackEvent("upsale", "purchase_success", {
        status: activeProduct?.isDiscount ? "discount" : "main",
        product: activeProduct?.productCode,
        productid: activeProduct?.solidgateId,
        amount: activeProduct?.amount,
        attempt: 0,
      });

      dispatch(nextStep());
      dispatch(setIsLoading(false));
    });
  };

  // Takes the whole provider error, not just message/code: the decline text sits
  // in the response body and is lost the moment the error is flattened.
  const onUpsaleError = (error: any) => {
    Analytics.trackEvent("upsale", "purchase_error", {
      status: activeProduct?.isDiscount ? "discount" : "main",
      product: activeProduct?.productCode,
      productid: activeProduct?.solidgateId,
      amount: activeProduct?.amount,
      attempt: 0,
      code: error?.code,
      message: extractPaymentErrorMessage(error),
    });

    dispatch(setIsLoading(false));
  };

  const handleOneClickFail = (error: any) => {
    onUpsaleError(error);

    onOneClickError(error);
  };

  const handleOneClickSuccess = async (data: OneTimePurchase) => {
    dispatch(
      setOneTimePurchases([
        ...oneTimePurchases,
        {
          product_code: data?.product_code,
          payment_service: data?.payment_service,
          currency: data?.currency,
        },
      ])
    );
    dispatch(setOneTimePurchaseId(data.id!));

    onUpsaleSuccess(purchase?.email);

    onSuccess(data);
  };

  const handlePaymentSuccess = async (p: Subscription) => {
    ppRef?.current?.closePaymentPopup();

    // (await dispatch(
    //   createOneClickFreePayment(
    //     p?.price_id || activeProduct?.productCode || "productCode",
    //     p?.transaction_id
    //   )
    // )) as any;
    await handleOneClickSuccess({
      product_code: p?.price_id || "productCode",
      payment_service: p.paymentSystem,
      currency: p.currency,
      order_id: p?.order?.order_id || p.transaction_id,
    }).finally(() => {
      onSuccess(purchase);
    });
  };

  const handlePaymentError = (error: any) => {
    onUpsaleError(error);

    onError &&
      onError({
        // The backend's text, not the raw `error.message`: on an axios failure
        // the latter is "Request failed with status code 400". Screens with their
        // own failure UI/events (e.g. change-payment-method) report this.
        message: extractPaymentErrorMessage(error),
        code: error?.code,
        paymentSystem: error?.paymentSystem || paymentSystem,
        currency: activeProduct?.currency,
        amount: activeProduct?.amount,
      });
  };

  const handleOneTimeSubmit = (formData: any) => {};

  const handlePayPalSubmit = () => {};

  return {
    handlePaymentSuccess,
    handlePaymentError,
    handleOneTimeSubmit,
    handlePayPalSubmit,
    handleOneClickSuccess,
    handleOneClickFail,
  };
};
